import { DownOutlined } from '@ant-design/icons';
import { ProFormField } from '@ant-design/pro-form';
import { useControllableValue } from 'ahooks';
import {
  Button,
  Dropdown,
  Input,
  Select,
  Space,
  Tooltip,
  SelectProps,
} from 'antd';
import { isEmpty } from 'lodash-es';

import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { getProductId, isElectron } from '@/infrastructure/product-info';
import { AreaFieldsPanelProps } from '@/presentation/components/form';
import {  MatchMode } from '@/presentation/components/form/standards/types';
import { Zone } from '@/proto/field';
import { KeyDataType } from '@/proto/token';
import { basicService, IIFilter } from '@/service/basic';
import { BaseFormItemProps } from '../type';
import styles from './index.module.scss';


export interface TdsAreaFieldsPanelProps extends BaseFormItemProps<any> {
  className?: string
  style?: CSSProperties;
  fieldScope: Zone;
}


export type AreaFieldValue = {
  operator?: string;
  key?: string;
  values?: string[] | string;
  type?: KeyDataType;
  name?: string;
};

export enum AreaNameEnum {
  ClientCountry='client_country', // 国家
  ClientProvince='client_province', // 省
  ClientCity='client_city', // 城市
  ElectronClientCountry='country', // 适配electron新的表字段名
  ElectronClientProvince='province',
  ElectronClientCity='city',
}


interface IOptions {
  value: string;
  label: string;
}


export const TdsArea: React.FC<TdsAreaFieldsPanelProps> = (props) => {
  const { className, fieldScope } = props;
  const [value, setValue] = useControllableValue<AreaFieldValue[]>(props, {
    defaultValue: [],
  });
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [countryOptions, setCountryOptions] = useState<IOptions[]>([]); // 国家下拉项
  const [provinceOptions, setProvinceOptions] = useState<IOptions[]>([]); // 省份下拉项
  const [cityOptions, setCityOptions] = useState<IOptions[]>([]); // 城市下拉项
  // 临时存储，点击确认的时候才触发form onchange
  const [customFilters, setCustomFilters] = useState<AreaFieldValue[]>(isEmpty(value) ? [] : value);

  // 根据electron环境动态选择字段名
  const clientCountry = isElectron() ? AreaNameEnum.ElectronClientCountry : AreaNameEnum.ClientCountry;
  const clientProvince = isElectron() ? AreaNameEnum.ElectronClientProvince : AreaNameEnum.ClientProvince;
  const clientCity = isElectron() ? AreaNameEnum.ElectronClientCity : AreaNameEnum.ClientCity;

  const showOverlay = () => setVisible(true);
  const hideOverlay = () => setVisible(false);


  const getFilterText = (values: AreaFieldValue[]) => values.map((item) => {
    if (Array.isArray(item?.values)) {
      return `[${item?.values?.join('、')}]`;
    }
    return `[${item?.values}]`;
  }).join(' / ');

  const onConfirm = async () => {
    try {
      hideOverlay();
      setValue(customFilters?.filter(item => item?.values?.length));
    } catch (e) {
    }
  };


  const filterText = useMemo(() => {
    if (!value || isEmpty(value)) return '';
    return getFilterText(value);
  }, [value]);


  const getOptions = async (dim: string, filter?: IIFilter[]): Promise<IOptions[]> => {
    const resp = await basicService.getMonitorFieldDistinctData({
      productID: getProductId(),
      dim,
      type: fieldScope,
      endTime: null,
      startTime: null,
      limit: 5000,
      optionalFilters: filter,
    });
    return resp?.map(value => ({ value, label: value }));
  };

  const load = async () => {
    const list = await getOptions(clientCountry);
    setCountryOptions(list);
    const provinceValues = await getOptions(clientProvince);
    setProvinceOptions(provinceValues);
    const cityValues = await getOptions(clientCity);
    setCityOptions(cityValues);
    setLoaded(true);
  };

  useEffect(() => {
    if (visible && !loaded) {
      load();
    }
  }, [visible]);

  useEffect(() => {
    setCustomFilters(value);
  }, [value]);


  // 国家选择事件
  const countryChange: SelectProps['onChange'] = async (value) => {
    setCustomFilters([{
      name: clientCountry,
      operator: MatchMode.OR,
      values: value,
    }]);
    setProvinceOptions([]);
    setCityOptions([]);
    if (value.length) {
      const provinceValues = await getOptions(
        clientProvince,
        [{ name: clientCountry, values: value, operator: MatchMode.OR }],
      );
      const cityValues = await getOptions(
        clientCity,
        [{ name: clientCountry, values: value, operator: MatchMode.OR }],
      );
      setProvinceOptions(provinceValues);
      setCityOptions(cityValues);
    }
  };

  // 省选择事件
  const provinceChange: SelectProps['onChange'] = async (value) => {
    setCustomFilters((list) => {
      const filterList = list.filter(item => item.name !== clientCity);
      if (list.find(item => item.name === clientProvince)) {
        return filterList.map((item) => {
          if (item.name === clientProvince) {
            return {
              name: clientProvince,
              operator: MatchMode.OR,
              values: value,
            };
          }
          return item;
        });
      }
      return [...filterList, {
        name: clientProvince,
        operator: MatchMode.OR,
        values: value,
      }];
    });
    setCityOptions([]);
    if (value.length) {
      const cityValues = await getOptions(
        clientCity,
        [{ name: clientProvince, values: value, operator: MatchMode.OR }],
      );
      setCityOptions(cityValues);
    }
  };

  // 城市选择事件
  const cityChange: SelectProps['onChange'] = async (value) => {
    setCustomFilters((list) => {
      if (list.find(item => item.name === clientCity)) {
        return list.map((item) => {
          if (item.name === clientCity) {
            return {
              name: clientCity,
              operator: MatchMode.OR,
              values: value,
            };
          }
          return item;
        });
      }
      return [...list, {
        name: clientCity,
        operator: MatchMode.OR,
        values: value,
      }];
    });
  };
  const countryValue = customFilters.find(item => item.name === clientCountry)?.values;
  const provinceValue = customFilters.find(item => item.name === clientProvince)?.values;
  const cityValue = customFilters.find(item => item.name === clientCity)?.values;
  const overlay = (
    <div className={styles.customOverlay}>
      <span>以下的关系为全且</span>
      <div className={styles.itemContainer}>
        <div>
          <Space size={8}>
            <Select
              showArrow={false}
              open={false}
              value='国家/地区'
              className={styles.column}
            />
            <Select
              options={[{
                value: MatchMode.OR,
                label: '是',
              }]}
              value={MatchMode.OR}
              className={styles.operator}
              placeholder='请选择'
            />
            <Select
              value={countryValue}
              mode={'multiple'}
              options={countryOptions}
              className={styles.values}
              placeholder='请选择'
              onChange={countryChange}
            />
          </Space>
        </div>
        <div>
          <Space size={8}>
            <Select
              className={styles.column}
              value='省份'
              showArrow={false}
              open={false}
            />
            <Select
              options={[{
                value: MatchMode.OR,
                label: '是',
              }]}
              value={MatchMode.OR}
              className={styles.operator}
              placeholder='请选择'
            />
            <Select
              value={provinceValue}
              mode={'multiple'}
              options={provinceOptions}
              className={styles.values}
              placeholder='请选择'
              onChange={provinceChange}
            />
          </Space>
        </div>
        <div>
          <Space size={8}>
            <Select
              className={styles.column}
              value='城市'
              showArrow={false}
              open={false}
            />
            <Select
              options={[{
                value: MatchMode.OR,
                label: '是',
              }]}
              value={MatchMode.OR}
              className={styles.operator}
              placeholder='请选择'
            />
            <Select
              value={cityValue}
              mode={'multiple'}
              options={cityOptions}
              className={styles.values}
              placeholder='请选择'
              onChange={cityChange}
            />
          </Space>
        </div>
      </div>
      <div className={styles.footer}>
        <Space size={10}>
          <Button onClick={hideOverlay}>取消</Button>
          <Button type={'primary'} onClick={onConfirm}>确定</Button>
        </Space>
      </div>
    </div>
  );


  return (
    <div className={className ? className : styles.panel} style={props.style}>
      <Space size={0} className={styles.panelSpace} direction={'vertical'}>
        <Tooltip title={filterText}>
          <Input
            placeholder='请选择'
            onFocus={showOverlay}
            suffix={<DownOutlined style={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '12px' }}/>}
            style={{ width: '100%' }}
            value={filterText}
          />
        </Tooltip>
        <Dropdown
          overlay={overlay}
          trigger={['click']}
          visible={visible}
          destroyPopupOnHide={false}
          onVisibleChange={visible => setVisible(visible)}
        >
          <div style={{ height: 1 }}/>
        </Dropdown>
      </Space>
    </div>
  );
};


export const AreaForm: React.FC<AreaFieldsPanelProps> = (props) => {
  const { name, label, hidden } = props;

  return (
    <div className={styles.tdsLabel}>
      <ProFormField name={name} label={label} hidden={hidden}>
        <TdsArea
          {...props}
        />
      </ProFormField>
    </div>
  );
};
